import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { keys } from '@/lib/query'
import { ScrollArea } from '@/components/ui/scroll-area'
import { RichContent } from '@/components/common/rich-content'
import { Skeleton } from '@/components/ui/skeleton'
import { ViewHeader } from '@/components/shell/view-header'
import { useWorkDocs } from '@/hooks/use-work-docs'

export default function Docs() {
  const { doc } = useParams()
  const docs = useWorkDocs()
  const entry = docs.find((d) => d.slug === doc)
  const file = entry?.file

  const { data, isPending, isError } = useQuery({
    queryKey: keys.workDoc(file),
    enabled: Boolean(file),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/work-files/${file}`)
      if (!res.ok) throw new Error(`doc fetch failed (${res.status})`)
      return res.text()
    },
  })

  if (!entry || !file) {
    return (
      <div className="text-muted-foreground grid h-full place-items-center text-sm">
        No doc called “{doc}” in n3oltd/work.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title={entry.title}
        meta={
          <span className="font-mono text-[11px]">
            n3oltd/work · {file} · latest from main
          </span>
        }
        chips={null}
      />
      <ScrollArea className="min-h-0 flex-1">
        <article className="mx-auto max-w-3xl p-6">
          {isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : isError ? (
            <p className="text-muted-foreground">Couldn't load {file} from GitHub.</p>
          ) : (
            <RichContent
              markdown={data}
              className="text-[13px] [&_table]:text-xs"
              components={{
                // Cross-doc links stay in-app; everything else opens on GitHub.
                a({ href, children }) {
                  const target = href ?? ''
                  const local = /^\.?\/?((?:guides\/)?[\w-]+\.md)(#.*)?$/.exec(target)
                  const linked = local ? docs.find((d) => d.file === local[1]) : undefined
                  if (linked) return <Link to={`/docs/${linked.slug}`}>{children}</Link>
                  return (
                    <a href={target} target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  )
                },
              }}
            />
          )}
        </article>
      </ScrollArea>
    </div>
  )
}
